export type Department = {
  id: number;
  name: string;
  children: Department[];
  blocked: boolean;
  connectedId: number | null;
  parentId: number | undefined;
  checked: boolean;
  collapsed: boolean;
  matched: boolean;
  level?: number;
};

export namespace Department {
  export const showFilters = (departments: Department[] = []): boolean => {
    return flattenDepartments(departments).length > 1;
  };

  export const flattenDepartments = (departments: Department[] = []): Department[] => {
    return (
      departments.reduce(
        (flat, department) => [...flat, department, ...flattenDepartments(department.children)],
        [] as Department[]
      ) || []
    );
  };

  // Names from the root down to `departmentId`, or `[]` if it isn't in the tree.
  export const findPath = (departments: Department[] = [], departmentId: number): string[] => {
    for (const department of departments) {
      if (department.id === departmentId) return [department.name];
      const childPath = findPath(department.children, departmentId);
      if (childPath.length > 0) return [department.name, ...childPath];
    }
    return [];
  };

  // Blocked children can't be selected, so they are skipped
  export const getSelectableDirectChildrenIds = (department: Department): number[] => {
    return department.children.filter((child) => !child.blocked).map((child) => child.id);
  };

  export const getSortedDepartmentsIds = (departments: Department[] = []): number[] => {
    return departments.map((department) => department.id).sort();
  };

  export const mergeDepartments = ({
    stateDepartmentsFlatten,
    payloadDepartments,
  }: {
    stateDepartmentsFlatten: Department[];
    payloadDepartments: Department[];
  }): Department[] => {
    return payloadDepartments.map((department) => {
      const matchedStateDepartment = stateDepartmentsFlatten.find(
        (stateDepartment) => stateDepartment.id === department.id
      );
      return matchedStateDepartment
        ? ({
            ...department,
            checked: matchedStateDepartment.checked,
            collapsed: matchedStateDepartment.collapsed,
            matched: matchedStateDepartment.matched,
            children: mergeDepartments({
              stateDepartmentsFlatten: stateDepartmentsFlatten,
              payloadDepartments: department.children,
            }),
          } as Department)
        : department;
    });
  };
}
